import { useEffect } from 'react';

export type ScrollBoundaryGuardOptions = {
	preventBackdropScroll?: boolean;
};

const useScrollBoundaryGuard = (
	ref: React.RefObject<HTMLElement | null>,
	isActive: boolean,
	options?: ScrollBoundaryGuardOptions,
) => {
	useEffect(() => {
		const el = ref.current;
		if (!el || !isActive) return;

		let touchStartY = 0;
		let activeScrollableEl: HTMLElement | null = null;

		const findClosestScrollable = (startNode: HTMLElement | null): HTMLElement | null => {
			let node: HTMLElement | null = startNode;
			while (node && el.contains(node)) {
				const style = window.getComputedStyle(node);
				const isScrollableY = /(auto|scroll)/.test(style.overflowY);
				const canScrollY = node.scrollHeight > node.clientHeight;
				if ((isScrollableY && canScrollY) || node.tagName === 'TEXTAREA') {
					return node;
				}
				node = node.parentElement as HTMLElement | null;
			}
			return null;
		};

		const handleTouchStart = (event: TouchEvent) => {
			if (event.touches && event.touches.length > 0) {
				touchStartY = event.touches[0].clientY;
				activeScrollableEl = findClosestScrollable(event.target as HTMLElement);

				const target = (activeScrollableEl || ref.current) as HTMLElement | null;
				if (target) {
					const maxScrollTop = target.scrollHeight - target.clientHeight;
					if (target.scrollTop <= 0) {
						target.scrollTop = 1;
					} else if (target.scrollTop >= maxScrollTop) {
						target.scrollTop = Math.max(maxScrollTop - 1, 0);
					}
				}
			}
		};

		const handleTouchMove = (event: TouchEvent) => {
			const target = (activeScrollableEl || ref.current) as HTMLElement | null;
			if (!target || !event.touches || event.touches.length === 0) return;

			const currentY = event.touches[0].clientY;
			const deltaY = currentY - touchStartY;

			const scrollTop = target.scrollTop;
			const maxScrollTop = target.scrollHeight - target.clientHeight;
			const cannotScroll = target.scrollHeight <= target.clientHeight;

			const atTopAndPullingDown = scrollTop <= 0 && deltaY > 0;
			const atBottomAndPullingUp = scrollTop >= maxScrollTop && deltaY < 0;

			if (cannotScroll || atTopAndPullingDown || atBottomAndPullingUp) {
				event.preventDefault();
			}
		};

		const containerEl = el.parentElement;
		const preventBackdropScroll = (event: TouchEvent) => {
			if (!el) return;
			const targetNode = event.target as Node | null;
			if (targetNode && !el.contains(targetNode)) {
				event.preventDefault();
				event.stopPropagation();
			}
		};

		el.addEventListener('touchstart', handleTouchStart, { passive: true });
		el.addEventListener('touchmove', handleTouchMove, { passive: false });
		if (options?.preventBackdropScroll) {
			containerEl?.addEventListener('touchmove', preventBackdropScroll, { passive: false });
		}

		return () => {
			el.removeEventListener('touchstart', handleTouchStart as EventListener);
			el.removeEventListener('touchmove', handleTouchMove as EventListener);
			if (options?.preventBackdropScroll) {
				containerEl?.removeEventListener('touchmove', preventBackdropScroll as EventListener);
			}
		};
	}, [ref, isActive, options?.preventBackdropScroll]);
};

export default useScrollBoundaryGuard;
